import React, { useEffect, useRef, useState, useCallback } from 'react';

const Slider = ({
  min,
  max,
  defaultMinValue,
  defaultMaxValue,
  onValuesChange,
  minRangeGap = 0, // Minimum allowed gap between min and max values
}) => {
  // Initialize currentMinValue, ensuring it's within bounds and respects defaultMaxValue if provided
  const [currentMinValue, setCurrentMinValue] = useState(() => {
    const initialMin = defaultMinValue ?? min;
    const initialMax = defaultMaxValue ?? max;
    // Ensure initialMin is not greater than initialMax - minRangeGap
    return Math.max(min, Math.min(initialMin, initialMax - minRangeGap, max));
  });

  const [currentMaxValue, setCurrentMaxValue] = useState(() => {
    const initialMin = defaultMinValue ?? min;
    const initialMax = defaultMaxValue ?? max;

    const derivedInitialMin = Math.max(min, Math.min(initialMin, initialMax - minRangeGap, max));
    return Math.min(max, Math.max(initialMax, derivedInitialMin + minRangeGap, min));
  });

  // State to track if thumbs are being dragged
  const [isDraggingMin, setIsDraggingMin] = useState(false);
  const [isDraggingMax, setIsDraggingMax] = useState(false);

  // Refs for the slider track and thumbs
  const sliderTrackRef = useRef(null);
  const minThumbRef = useRef(null);
  const maxThumbRef = useRef(null);

  // Calculates the percentage position of a value within the min/max range.
  const getPercentage = useCallback(
    (value) => {
      const range = max - min;
      if (range === 0) return value >= max ? 100 : 0;
      const clampedValue = Math.max(min, Math.min(max, value));
      return ((clampedValue - min) / range) * 100;
    },
    [min, max]
  );

  // Effect to update visual position of the min thumb
  useEffect(() => {
    if (minThumbRef.current) {
      const percentage = getPercentage(currentMinValue);
      minThumbRef.current.style.left = `${percentage}%`;
      minThumbRef.current.style.zIndex = isDraggingMin ? '20' : '10';
    }
  }, [currentMinValue, getPercentage, isDraggingMin]);

  // Effect to update visual position of the max thumb
  useEffect(() => {
    if (maxThumbRef.current) {
      const percentage = getPercentage(currentMaxValue);
      maxThumbRef.current.style.left = `${percentage}%`;
      maxThumbRef.current.style.zIndex = isDraggingMax ? '20' : '10';
    }
  }, [currentMaxValue, getPercentage, isDraggingMax]);

  /**
   * Calculates the new slider value based on the clientX position of a mouse/touch event.
   */
  const getValueFromClientX = useCallback(
    (clientX) => {
      if (!sliderTrackRef.current) {
        // Fallback if track is not yet available, though this should ideally not happen in normal flow
        return min;
      }
      const rect = sliderTrackRef.current.getBoundingClientRect();
      const offsetX = clientX - rect.left;
      const trackWidth = rect.width;

      if (trackWidth === 0) return min;

      let percentage = offsetX / trackWidth;
      percentage = Math.max(0, Math.min(1, percentage));

      let newValue = min + percentage * (max - min);
      // Clamping to overall min/max is implicitly handled by percentage clamp
      // and final clamping in handlers.
      return newValue;
    },
    [min, max]
  );

  // --- Min Thumb Drag Handlers ---
  const handleMinDragStart = useCallback((event) => {
    if (event.type === 'mousedown') event.preventDefault();
    setIsDraggingMin(true);
    minThumbRef.current?.focus();
  }, []);

  const handleMinDragMove = useCallback(
    (event) => {
      if (!isDraggingMin) return;
      if (event.type === 'touchmove') event.preventDefault();

      const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX;
      const rawNewValue = getValueFromClientX(clientX);

      // Clamp new min value: must be >= min, and <= currentMaxValue - minRangeGap
      const newMinValue = Math.max(min, Math.min(rawNewValue, currentMaxValue - minRangeGap));

      setCurrentMinValue(newMinValue);
      if (onValuesChange) {
        onValuesChange({ minValue: newMinValue, maxValue: currentMaxValue });
      }
    },
    [isDraggingMin, getValueFromClientX, currentMaxValue, min, minRangeGap, onValuesChange]
  );

  const handleMinDragEnd = useCallback(() => {
    if (isDraggingMin) {
      setIsDraggingMin(false);
      // TODO: Implement onValuesChangeCommitted
    }
  }, [isDraggingMin]);

  // --- Max Thumb Drag Handlers ---
  const handleMaxDragStart = useCallback((event) => {
    if (event.type === 'mousedown') event.preventDefault();
    setIsDraggingMax(true);
    maxThumbRef.current?.focus();
  }, []);

  const handleMaxDragMove = useCallback(
    (event) => {
      if (!isDraggingMax) return;
      if (event.type === 'touchmove') event.preventDefault();

      const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX;
      const rawNewValue = getValueFromClientX(clientX);

      // Clamp new max value: must be <= max, and >= currentMinValue + minRangeGap
      const newMaxValue = Math.min(max, Math.max(rawNewValue, currentMinValue + minRangeGap));

      setCurrentMaxValue(newMaxValue);
      if (onValuesChange) {
        onValuesChange({ minValue: currentMinValue, maxValue: newMaxValue });
      }
    },
    [isDraggingMax, getValueFromClientX, currentMinValue, max, minRangeGap, onValuesChange]
  );

  const handleMaxDragEnd = useCallback(() => {
    if (isDraggingMax) {
      setIsDraggingMax(false);
      // TODO: Implement onValuesChangeCommitted
    }
  }, [isDraggingMax]);

  // Effect for MIN thumb global drag listeners
  useEffect(() => {
    const ownerDocument = sliderTrackRef.current?.ownerDocument || document;
    if (isDraggingMin) {
      ownerDocument.addEventListener('mousemove', handleMinDragMove);
      ownerDocument.addEventListener('mouseup', handleMinDragEnd);
      ownerDocument.addEventListener('touchmove', handleMinDragMove, {
        passive: false,
      });
      ownerDocument.addEventListener('touchend', handleMinDragEnd);
      ownerDocument.addEventListener('touchcancel', handleMinDragEnd);
    }
    return () => {
      ownerDocument.removeEventListener('mousemove', handleMinDragMove);
      ownerDocument.removeEventListener('mouseup', handleMinDragEnd);
      ownerDocument.removeEventListener('touchmove', handleMinDragMove);
      ownerDocument.removeEventListener('touchend', handleMinDragEnd);
      ownerDocument.removeEventListener('touchcancel', handleMinDragEnd);
    };
  }, [isDraggingMin, handleMinDragMove, handleMinDragEnd]);

  // Effect for MAX thumb global drag listeners
  useEffect(() => {
    const ownerDocument = sliderTrackRef.current?.ownerDocument || document;
    if (isDraggingMax) {
      ownerDocument.addEventListener('mousemove', handleMaxDragMove);
      ownerDocument.addEventListener('mouseup', handleMaxDragEnd);
      ownerDocument.addEventListener('touchmove', handleMaxDragMove, {
        passive: false,
      });
      ownerDocument.addEventListener('touchend', handleMaxDragEnd);
      ownerDocument.addEventListener('touchcancel', handleMaxDragEnd);
    }
    return () => {
      ownerDocument.removeEventListener('mousemove', handleMaxDragMove);
      ownerDocument.removeEventListener('mouseup', handleMaxDragEnd);
      ownerDocument.removeEventListener('touchmove', handleMaxDragMove);
      ownerDocument.removeEventListener('touchend', handleMaxDragEnd);
      ownerDocument.removeEventListener('touchcancel', handleMaxDragEnd);
    };
  }, [isDraggingMax, handleMaxDragMove, handleMaxDragEnd]);

  // Calculate percentages for the fill element
  const minFillPercentage = getPercentage(currentMinValue);
  const maxFillPercentage = getPercentage(currentMaxValue);
  const fillWidthPercentage = Math.max(0, maxFillPercentage - minFillPercentage);

  return (
    <div className="w-full py-3 px-1">
      <div
        ref={sliderTrackRef}
        className="w-full h-[4px] bg-gray-300 rounded-full relative m-1 cursor-pointer group"
        onClick={(event) => {
          if (isDraggingMin || isDraggingMax || !sliderTrackRef.current) return;
          if (
            (minThumbRef.current && minThumbRef.current.contains(event.target)) ||
            (maxThumbRef.current && maxThumbRef.current.contains(event.target))
          ) {
            return; // Click was on a thumb
          }

          const clickedValue = getValueFromClientX(event.clientX);
          let newCommittedMinValue = currentMinValue;
          let newCommittedMaxValue = currentMaxValue;

          // Determine which thumb is closer to the click
          if (
            Math.abs(clickedValue - currentMinValue) <= Math.abs(clickedValue - currentMaxValue)
          ) {
            // Move min thumb
            newCommittedMinValue = Math.max(
              min,
              Math.min(clickedValue, currentMaxValue - minRangeGap)
            );
            setCurrentMinValue(newCommittedMinValue);
          } else {
            // Move max thumb
            newCommittedMaxValue = Math.min(
              max,
              Math.max(clickedValue, currentMinValue + minRangeGap)
            );
            setCurrentMaxValue(newCommittedMaxValue);
          }

          if (onValuesChange) {
            const finalMin =
              Math.abs(clickedValue - currentMinValue) <= Math.abs(clickedValue - currentMaxValue)
                ? newCommittedMinValue
                : currentMinValue;
            const finalMax =
              Math.abs(clickedValue - currentMinValue) <= Math.abs(clickedValue - currentMaxValue)
                ? currentMaxValue
                : newCommittedMaxValue;
            onValuesChange({ minValue: finalMin, maxValue: finalMax });
          }
        }}
      >
        {/* Fill element */}
        <div
          className="absolute top-0 h-full bg-SLIGreen rounded-full"
          style={{
            left: `${minFillPercentage}%`,
            width: `${fillWidthPercentage}%`,
          }}
        />

        {/* Min Thumb */}
        <div
          ref={minThumbRef}
          className="absolute w-4 h-4 bg-SLIGreen rounded-full cursor-grab shadow-md group-hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-SLIGreen2 focus:ring-opacity-75 active:scale-110"
          style={{
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          onMouseDown={handleMinDragStart}
          onTouchStart={handleMinDragStart}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={currentMaxValue - minRangeGap} // Max value for min thumb
          aria-valuenow={currentMinValue}
          aria-orientation="horizontal"
          aria-label="Minimum value"
          tabIndex={0}
        />

        {/* Max Thumb */}
        <div
          ref={maxThumbRef}
          className="absolute w-4 h-4 bg-SLIGreen rounded-full cursor-grab shadow-md group-hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-SLIGreen2 focus:ring-opacity-75 active:scale-110"
          style={{
            top: '50%',
            transform: 'translate(-50%, -50%)',
            // `left` and `zIndex` are applied by useEffect
          }}
          onMouseDown={handleMaxDragStart}
          onTouchStart={handleMaxDragStart}
          role="slider"
          aria-valuemin={currentMinValue + minRangeGap} // Min value for max thumb
          aria-valuemax={max}
          aria-valuenow={currentMaxValue}
          aria-orientation="horizontal"
          aria-label="Maximum value"
          tabIndex={0}
        />
      </div>

      <div className="mt-2 flex justify-between text-md font-medium">
        <div className="flex flex-col">
          <span className="text-SLIGreen">Min</span>
          <span className="text-SLIBlack">${currentMinValue.toFixed(0)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-SLIGreen">Max</span>
          <span className="text-SLIBlack">${currentMaxValue.toFixed(0)}</span>
        </div>
      </div>
    </div>
  );
};

export { Slider };
